import { EmptyState, RadioGroup, Skeleton } from '../ui';
import type { RadioOption } from '../ui';
import { listEligibleAssignees, type EligibleAssignee } from '../../lib/api/inspections.gen';
import { useCursorList } from '../../lib/query/useCursorList';
import { eligibleAssigneesKey, noEligibleAssigneeCopy } from '../../lib/inspectionAssignees';
import { errorMessage } from '../../lib/errorMessage';

// AssigneePicker (P05 §2, §8; W4; story 5-2a) — the human client of
// `GET /v1/inspections/eligible-assignees`.
//
// ── The one rule this component is built around ───────────────────────────
// W4's acceptance criteria: "the assignee dropdown never lists them
// (server-filtered endpoint, not client filter)". So there is NO `.filter()` in
// this file and there never may be. Every name the server returns is offered;
// nobody else is. If this component were bypassed entirely, `POST /:id/assign`
// still answers 403 `assignee_out_of_scope` — the picker cannot grant anything,
// and it must not be able to hide anything either, because a client-side filter
// is a rule that drifts from the server's the moment either changes.
//
// That is also why an ineligible person is ABSENT here rather than disabled with
// a reason — the opposite of the sibling `TypePicker`. The difference is not
// cosmetic: a type the caller may not perform is the caller's own limitation and
// telling them is useful; a COLLEAGUE they may not direct is somebody else's
// record, and rendering "Dana Chen — outside your team" would leak the shape of
// the org chart to someone the org deliberately scoped away from it.
//
// Shared by the create wizard (`modules/scheduling`) and the reassign dialog
// (shell). Same control, same query key, one behaviour.

export interface AssigneePickerProps {
  /** The procedure whose `allowedRoleIds.perform` decides the role half. */
  inspectionTypeId?: string | null;
  /** For a REASSIGN: the server reads the type off this row instead. */
  inspectionId?: string | null;
  value: string | null;
  onChange: (userId: string | null) => void;
  disabled?: boolean;
  /** Rendered above the list — the reassign dialog names the incumbent here. */
  name?: string;
}

export function AssigneePicker({
  inspectionTypeId,
  inspectionId,
  value,
  onChange,
  disabled,
  name = 'assignedTo',
}: AssigneePickerProps): JSX.Element {
  const filters = {
    ...(inspectionTypeId ? { inspectionTypeId } : {}),
    ...(inspectionId ? { inspectionId } : {}),
  };
  const enabled = Boolean(inspectionTypeId || inspectionId);

  const { items, isLoading, isError, error, refetch } = useCursorList<EligibleAssignee>({
    queryKey: eligibleAssigneesKey(filters),
    fetchPage: (page) => listEligibleAssignees({ ...page, ...filters }),
    limit: 50,
    enabled,
    // Live convergence, for the same reason the TypePicker asks for it (3-3b):
    // this list is an AUTHORIZATION answer, not a page of content. A membership
    // suspended, a role revoked or a team moved in another session must stop
    // being offered here without a page reload.
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  if (!enabled) {
    const copy = noEligibleAssigneeCopy(false);
    return (
      <p className="text-sm text-muted-foreground" data-testid="assignee-picker-no-type">
        {copy.description}
      </p>
    );
  }

  if (isLoading) return <Skeleton className="h-20 w-full" />;

  if (isError) {
    return (
      <EmptyState
        headingLevel={4}
        title="The list of people could not be loaded"
        // G-27: the SERVER's sentence, through the one unwrap point.
        description={errorMessage(error)}
        actionLabel="Retry"
        onAction={refetch}
      />
    );
  }

  if (items.length === 0) {
    const copy = noEligibleAssigneeCopy(true);
    return (
      <div data-testid="assignee-picker-empty">
        <EmptyState headingLevel={4} title={copy.title} description={copy.description} />
      </div>
    );
  }

  const options: RadioOption[] = items.map((person) => ({
    value: person.userId,
    label: person.isCurrentAssignee ? `${person.name} (assigned now)` : person.name,
    ...(disabled ? { disabled: true } : {}),
    description: [
      person.roleNames.join(', ') || null,
      person.teamName,
      // The workload P05 §2 asks the action-proposer to weigh, shown to the human
      // who is making the same decision.
      person.openAssignments === 1 ? '1 open job' : `${person.openAssignments} open jobs`,
    ]
      .filter(Boolean)
      .join(' · '),
  }));

  return (
    <RadioGroup
      name={name}
      id="assignee-picker"
      value={value ?? undefined}
      onChange={(event) => onChange(event.target.value)}
      options={options}
      {...(disabled ? { disabled: true } : {})}
    />
  );
}
